import React from "react";
import { useNavigate } from "react-router-dom";
import { destinationIcons, sourceIcons } from "./connector/data";

const AnimatedConnectionFlow: React.FC = () => {
  const navigate = useNavigate();

  function createConnection() {
    navigate("/create/connections");
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8 relative overflow-hidden">
      <div className="lg:w-[68%] xl:w-[50%] w-full">
        {/* Background stars */}
        <div className="absolute inset-0">
          {[...Array(100)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-0.5 bg-white rounded-full opacity-30"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
              }}
            />
          ))}
        </div>

        <div className="relative w-full max-w-6xl h-96">
          {/* Sources Section */}
          <div className="absolute left-0 top-0 h-full flex flex-col justify-center">
            <h3 className="text-gray-400 font-medium mb-6 text-sm">Sources</h3>
            <div className="relative h-80 space-y-4">
              {sourceIcons.map((source) => (
                <div
                  key={source.id}
                  className="absolute left-0"
                  style={{ top: source.top }}
                >
                  {source.component}
                </div>
              ))}
            </div>
          </div>

          {/* Destinations Section */}
          <div className="absolute right-0 top-0 h-full flex flex-col justify-center">
            <h3 className="text-gray-400 font-medium mb-6 text-sm">
              Destinations
            </h3>
            <div className="relative h-80 space-y-4">
              {destinationIcons.map((dest) => (
                <div
                  key={dest.id}
                  className="absolute right-0"
                  style={{ top: dest.top }}
                >
                  {dest.component}
                </div>
              ))}
            </div>
          </div>

          {/* Central Hub with DALM Logo */}
          <div className="absolute z-50 left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="relative">
              {/* Gradient background */}
              <div
                className="w-32 h-32 rounded-full bg-black bg-opacity-20 flex items-center justify-center backdrop-blur-sm"
                style={{
                  filter: `
          drop-shadow(0 0 8px rgba(59, 130, 246, 0.5)) 
          drop-shadow(0 0 8px rgba(147, 51, 234, 0.5)) 
          drop-shadow(0 0 8px rgba(236, 72, 153, 0.5))
        `,
                }}
              >
                <img
                  src="https://ik.imagekit.io/anvgrd1ahnys/DALM/Dalm%20Logos%20IMGKIT/favicon-dalm-test/apple-touch-icon_Hg0-6reJdv.png?updatedAt=1750176559662"
                  alt="DALM Logo"
                  className="w-24 h-24 object-contain"
                />
              </div>
            </div>
          </div>

          {/* Animated Connection Lines */}
          <svg className="absolute inset-0 w-[90%] md:w-full h-full pointer-events-none">
            <defs>
              <linearGradient
                id="flowGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8" />
                <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#ec4899" stopOpacity="0.8" />
              </linearGradient>
            </defs>

            {/* Main connection path - curved line from left to right through center */}
            <path
              id="mainPath"
              d="M 72 150 Q 200 100 384 192 Q 568 284 696 240"
              stroke="url(#flowGradient)"
              strokeWidth="2"
              fill="none"
              className="opacity-60"
            />

            {/* Animated dot traveling the entire path */}
            <circle
              r="6"
              fill="url(#flowGradient)"
              className="opacity-90 drop-shadow-lg"
            >
              <animateMotion dur="4s" repeatCount="indefinite" rotate="auto">
                <mpath href="#mainPath" />
              </animateMotion>
            </circle>

            {/* Glowing trail effect */}
            <circle r="3" fill="#ffffff" className="opacity-50">
              <animateMotion
                dur="4s"
                repeatCount="indefinite"
                rotate="auto"
                begin="0.1s"
              >
                <mpath href="#mainPath" />
              </animateMotion>
            </circle>
          </svg>

          {/* Bottom CTA Button */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-20">
            <button
              onClick={createConnection}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-medium shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 border border-blue-500"
            >
              Create your first connection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedConnectionFlow;
